import { client } from '@api/generated/client.gen';
import { getAdminUser } from '@api/generated/sdk.gen';
import { useQueries } from '@tanstack/react-query';

import { parseContentDispositionFilename } from './lib';

/**
 * Открывает пруф передачи в новой вкладке.
 *
 * Эндпоинт `GET /api/agreements/{id}/proofs/{proofId}` требует Bearer-токен,
 * поэтому обычная ссылка не подходит: качаем blob через общий axios-инстанс
 * (интерсепторы авторизации уже навешаны) и открываем object-URL.
 */
export async function openProof(agreementId: string, proofId: string): Promise<void> {
  const res = await client.instance.get<Blob>(`/api/agreements/${agreementId}/proofs/${proofId}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  window.open(url, '_blank', 'noopener');
  // отложенно освобождаем — вкладке нужно успеть открыть URL
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}

/**
 * Сохраняет blob на диск через временный `<a download>`.
 * Object-URL освобождается сразу после клика — браузер уже начал скачивание.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Скачивает доказательный пакет по договору
 * (`GET /api/admin/agreements/{id}/evidence-package`, право `dispute.evidence.export`).
 *
 * Ответ — ZIP с файлами договора, пруфами, цепочками аудита и `manifest.json`
 * с результатами проверок; собирается на момент запроса, поэтому не кешируется.
 * Идём через общий axios-инстанс (Bearer-интерсепторы уже навешаны) с
 * `responseType: 'blob'`; имя файла — из `Content-Disposition`, фолбэк
 * `evidence-agreement-{id}.zip`. Ошибки (403/404) пробрасываются вызывающему.
 */
export async function downloadEvidencePackage(agreementId: string): Promise<string> {
  const res = await client.instance.get<Blob>(
    `/api/admin/agreements/${agreementId}/evidence-package`,
    { responseType: 'blob' },
  );
  const header = res.headers['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(header) ?? `evidence-agreement-${agreementId}.zip`;
  saveBlob(res.data, filename);
  return filename;
}

/**
 * Расшифровывает идентификаторы акторов в телефоны через
 * `GET /api/admin/users/{id}` (ключ кэша общий с карточкой пользователя).
 *
 * Возвращает `id → телефон`; без права `users.read` или для неизвестного id
 * записи в мапе нет — вызывающий показывает укороченный id.
 */
export function useActorPhones(ids: string[]): Record<string, string> {
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['admin-user', id],
      queryFn: async () => {
        const res = await getAdminUser({ path: { id } });
        return res.data ?? null;
      },
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const map: Record<string, string> = {};
  queries.forEach((q, idx) => {
    const id = ids[idx];
    const phone = q.data?.phone;
    if (id && phone) map[id] = phone;
  });
  return map;
}

/** Укороченный идентификатор для показа, когда телефон недоступен. */
export function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}
